'use client'

import { createContext, useState, useEffect } from 'react'

import { sampleImagesCreate } from '../config/sampleImagesCreate'
import { useAuth } from './AuthContext'
import {
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
} from '../helpers/safeStorage'
import {
  VIDEO_V4,
  VIDEO_V5,
  VIDEO_MODEL_AUTO,
  resolveVideoVersion,
  LTX_DIALOGUE_SPEAKER_DEFAULT,
  LTX_DIALOGUE_TONE_DEFAULT,
  LTX_DIALOGUE_ACCENT_DEFAULT,
  normalizeTone,
  normalizeAccent,
  LTX_LENGTH_DEFAULT,
  LTX_FPS_DEFAULT,
  LTX_RESOLUTION_DEFAULT,
  normalizeResolution,
} from '../helpers/videoVersions'
import {
  asBool,
  asHttpUrl,
  asNumber,
  usePersistedState,
} from '../helpers/usePersistedState'

export const CreateContext = createContext('')

// How many recently-applied AI Character reference images we keep on hand for
// quick re-apply in the AI Character modal.
const RECENT_CHARACTERS_LIMIT = 15

// The built-in preset faces are always shown, so we exclude them from the
// "recent" history — that row is only worth surfacing custom faces the user
// brought in (e.g. via "use as character").
const PRESET_CHARACTER_URLS = sampleImagesCreate.map((img) => img.imageUrl)

const randomSeed = () => Math.floor(Math.random() * 4000000000).toString()

// A model taken out of the picker can still be someone's remembered choice, and the backend
// would refuse it. Send them to its nearest model instead. Hardcore Flame (LUSTIFY) was
// withdrawn 2026-10-01 for its licence; the backend keeps it dormant so it can come back.
// Fur and Furtoon were hidden 2026-10-06. The backend still serves them, but a remembered one — or
// one restored by Clone or a saved prompt, which also go through asLiveModel — would keep showing a
// model the picker no longer offers. Anime is their legacy SD sibling: open to every
// plan, the same 1 gem, also stylised, and allowed on the video tab — nobody is locked out or
// charged more. (Hyperanime is closer in look but InputsVideo disables it.)
const RETIRED_MODELS = {
  'Hardcore Flame': 'Cinematic Flame',
  Fur: 'Anime',
  Furtoon: 'Anime',
}
export const asLiveModel = { decode: (raw) => RETIRED_MODELS[raw] ?? raw }

// Default-model A/B, phase 2 (from 2026-10-02; backend docs/experiments/log.md "Still running").
// A NEW account (created in the last 24 h) whose Create model is still the untouched default starts
// on Pony Anime XL (arm 0, control — the worst-rated model, 77% of its voters mostly down) or
// Pony XL 2 (arm 1, 44%). Applied once per browser and uid, so a later choice is never overridden.
// The arm is never stored — the readout recomputes it: FNV-1a 32-bit of `default_model_ab2:<uid>`,
// bit 16. (Phase 1, 2026-10-01, is void: it only reached browsers with no remembered model, which
// almost no new account has, and `% 2` of FNV-1a is the parity of the uid's odd bytes.)
export const DEFAULT_MODEL_ARMS = ['Pony Anime XL', 'Pony XL 2']
export const defaultModelArm = (uid) => {
  let h = 0x811c9dc5
  for (const b of new TextEncoder().encode(`default_model_ab2:${uid}`)) {
    h ^= b
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return (h >>> 16) & 1
}
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000

export const CreateProvider = ({ children }) => {
  // Input settings — persisted to localStorage (namespaced 'create_*') so a
  // refresh keeps the user's last choices. usePersistedState mirrors each value.
  // The main prompt text stays in-memory (plain useState) on purpose.
  const [model, setModel] = usePersistedState(
    'model',
    DEFAULT_MODEL_ARMS[0],
    asLiveModel,
  )
  const { user } = useAuth()
  useEffect(() => {
    if (!user?.uid) return
    const created = Date.parse(user.metadata?.creationTime || '')
    if (!created || Date.now() - created > NEW_ACCOUNT_MS) return
    const flag = `create_model_ab2_${user.uid}`
    if (safeGetItem(flag)) return
    safeSetItem(flag, '1')
    const armDefault = DEFAULT_MODEL_ARMS[defaultModelArm(user.uid)]
    setModel((current) =>
      current === DEFAULT_MODEL_ARMS[0] ? armDefault : current,
    )
  }, [user, setModel])
  const [imageSize, setImageSize] = usePersistedState('imageSize', '512x768')
  const [quality, setQuality] = usePersistedState('quality', 'Ultra')
  const [creativity, setCreativity] = usePersistedState(
    'creativity',
    50,
    asNumber,
  )
  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = usePersistedState(
    'negativePrompt',
    '',
  )
  const [seed, setSeed] = usePersistedState('seed', randomSeed)
  const [seedVideo, setSeedVideo] = usePersistedState('seedVideo', randomSeed)
  const [filter, setFilter] = usePersistedState('filter', 'Default')
  const [imageSrc, setImageSrc] = useState([])
  const [videoSrc, setVideoSrc] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [lora, setLora] = usePersistedState('lora', 'Default')
  const [lorasList, setLorasList] = useState([])
  const [lorasListVideo, setLorasListVideo] = useState([])

  const [docRef, setDocRef] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [locked, setLocked] = usePersistedState('locked', false, asBool)
  const [activeTab, setActiveTab] = useState('prompt')
  const [ntier, setNtier] = usePersistedState('ntier', 1, asNumber)
  const [faceFix, setFaceFix] = usePersistedState('faceFix', false, asBool)
  const [imageHistory, setImageHistory] = useState([])
  const [docRefHistory, setDocRefHistory] = useState([])
  const [isUpscaling, setIsUpscaling] = useState(false)
  const [safeImage, setSafeImage] = useState(false)
  const [brushSize, setBrushSize] = useState(50)
  const [undo, setUndo] = useState(false)
  const [reset, setReset] = useState(false)
  const [currentImageSrcIndex, setCurrentImageSrcIndex] = useState(-1)
  const [mask, setMask] = useState(null)
  const [brushStrength, setBrushStrength] = useState(0.7)
  const [hideMask, setHideMask] = useState(false)
  const [tool, setTool] = useState('select')
  const [cooldown, setCooldown] = useState(0)
  const [cooldownProgress, setCooldownProgress] = useState(0)
  const [initialCooldown, setInitialCooldown] = useState(0)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [blurryImage, setBlurryImage] = useState(false)
  const [hasMadeChanges, setHasMadeChanges] = useState(false)
  const [details, setDetails] = usePersistedState('details', 0, asNumber)
  const [customPoseImage, setCustomPoseImage] = useState(null)
  const [charImage, setCharImage] = usePersistedState(
    'charImage',
    null,
    asHttpUrl,
  )
  // Recently applied AI Character reference images (hosted URLs), most-recent
  // first, deduped and capped. Persisted so users can quickly reapply a face
  // they've used before. Populated automatically whenever charImage changes to
  // a hosted URL (see effect below), so every entry point — presets, "use as
  // character", the image page — records without extra wiring.
  const [recentCharacters, setRecentCharacters] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return JSON.parse(safeGetItem('recentCharacters')) || []
      } catch {
        return []
      }
    }
    return []
  })
  // Maps a hosted character-image URL → the Firestore image doc id it came
  // from ("use character" on an image/video page or card). Generation looks up
  // the current charImage here and sends the id so the backend can save which
  // image the character originated from (character_image_id) and the image
  // page can link back to it. Entry points that don't know a source doc
  // (uploads, presets, old recents) simply have no mapping → no id is sent.
  // Kept as a URL-keyed map rather than a second piece of state so a charImage
  // set anywhere without id wiring can never pair with a stale id.
  const [charImageIds, setCharImageIds] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return JSON.parse(safeGetItem('charImageIds')) || {}
      } catch {
        return {}
      }
    }
    return {}
  })
  const [charImageVideo, setCharImageVideo] = useState(null)
  const [videoRefLora, setVideoRefLora] = useState(null)
  const [videoId, setVideoId] = useState(null)
  const [animation, setAnimation] = usePersistedState('animation', 4, asNumber)
  // 'auto' = AI-chosen motion, 'prompt' = user-described motion. Lives in context so
  // the Animate modal, the Video tab, and handleSubmitVideoV3 all read one value.
  const [animateMode, setAnimateMode] = usePersistedState('animateMode', 'auto')
  const [videoModel, setVideoModel] = usePersistedState(
    'videoModel',
    'Real',
    asLiveModel,
  )
  const [videoImageSize, setVideoImageSize] = useState('512x768')
  // Persisted, like the negative prompt right below it. These two are a pair — the motion
  // prompt being the ONE half that evaporated on reload is what made switching the Create
  // layout (a full reload) silently drop a typed motion line while the persisted `dialogue`
  // survived. A surviving dialogue line then flips the render to the voice model via
  // resolveVideoVersion, so the character SPEAKS the motion text. Symmetry is the fix.
  //
  // Note this is deliberately unlike the image tab's `prompt`, which stays in memory: there
  // the negative prompt is the only persisted half, so nothing derives a MODEL from it.
  const [videoPrompt, setVideoPrompt] = usePersistedState('videoPrompt', '')
  const [videoNegativePrompt, setVideoNegativePrompt] = usePersistedState(
    'videoNegativePrompt',
    '',
  )
  const [videoIsLoading, setVideoIsLoading] = useState(false)
  const [videoLora, setVideoLora] = usePersistedState('videoLora', 'Default')
  const [videoFilter, setVideoFilter] = usePersistedState(
    'videoFilter',
    'Default',
  )
  const [videoCreativity, setVideoCreativity] = usePersistedState(
    'videoCreativity',
    50,
    asNumber,
  )
  const [expandAmount, setExpandAmount] = useState(1)
  const [fullMask, setFullMask] = useState(null)
  const [cameraMotion, setCameraMotion] = usePersistedState(
    'cameraMotion',
    'still',
  )
  const [lastQuality, setLastQuality] = useState('Ultra')
  const [characterSuggestion, setCharacterSuggestion] = useState('')
  const [locationSuggestion, setLocationSuggestion] = useState('')
  const [styleSuggestion, setStyleSuggestion] = useState('')
  const [actionSuggestion, setActionSuggestion] = useState('')
  // Custom Pose mode: false = Depth (ControlNet depth, replicates fine detail),
  // true = Position (openpose, pose only). Studio+ feature.
  const [stickMode, setStickMode] = useState(false)
  const [folderSetting, setFolderSetting] = useState('Gallery')
  const [folders, setFolders] = useState(['Gallery'])
  const [chillMode, setChillMode] = useState(false)
  const [formatTab, setFormatTab] = useState('photo')
  const [emotion, setEmotion] = usePersistedState('emotion', 'Default')
  // AI Character apply version. This starting value is normalized on mount by
  // the model-change effect in Inputs, which defaults to the strongest version
  // the current model supports; a manual pick then persists across model changes
  // until it becomes incompatible. The choice is intentionally not persisted
  // across sessions.
  const [characterVersion, setCharacterVersion] = useState('v2')
  const [detectionPrompt, setDetectionPrompt] = useState('')
  const [avoidancePrompt, setAvoidancePrompt] = useState('')
  // How the video model is CHOSEN — 'auto' (default), or a forced VIDEO_V4 / VIDEO_V5. The model
  // that actually renders is derived below, once `dialogue` exists, because Auto reads it.
  //
  // The reset marker is new: everyone, including users who explicitly picked v4 or v5 under the
  // old switcher, lands on Auto once. That is deliberate — the old choice was made between two
  // version numbers that no longer appear in the UI, so carrying it forward would pin users to a
  // model they never knowingly chose.
  const [videoModelMode, setVideoModelMode] = usePersistedState(
    'videoModelMode',
    VIDEO_MODEL_AUTO,
    { resetOnce: 'videoModelModeAuto' },
  )
  // video5-only inputs. Held here (not in InputsVideo) because the Video tab, the Animate modal
  // and handleSubmitVideoV3 all read the same values, exactly like animateMode.
  //
  // All of them persist, including the dialogue line. An earlier version kept the line in plain
  // useState on the theory that a stale sentence is worse than an empty box — but that made it
  // the ONE video setting that didn't survive a refresh, which is the more surprising behaviour.
  // Reset clears it like everything else.
  const [dialogue, setDialogue] = usePersistedState('dialogue', '')
  const [dialogueSpeaker, setDialogueSpeaker] = usePersistedState(
    'dialogueSpeaker',
    LTX_DIALOGUE_SPEAKER_DEFAULT,
  )
  // decode, not a plain read: the preset list was cut from seven to four, so a browser can be
  // holding a value ('cute', 'anime') that no longer has an <option> — which renders the select
  // blank. normalizeTone folds it to the survivor in the same acoustic corner, matching what the
  // backend does with the same value.
  const [dialogueTone, setDialogueTone] = usePersistedState(
    'dialogueTone',
    LTX_DIALOGUE_TONE_DEFAULT,
    { decode: normalizeTone },
  )
  const [dialogueAccent, setDialogueAccent] = usePersistedState(
    'dialogueAccent',
    LTX_DIALOGUE_ACCENT_DEFAULT,
    { decode: normalizeAccent },
  )

  // The model that will actually render. DERIVED, never stored: the stored thing is the user's
  // intent (`videoModelMode`), and a line typed into the dialogue box changes what that intent
  // resolves to. Keeping it derived is what makes "type a line -> the voice model is used"
  // impossible to get out of sync, including on the submit path.
  const videoVersion = resolveVideoVersion(videoModelMode, dialogue)
  // Existing callers (Clone, the pose picker's v4 fallback, deep links) say "use THIS version".
  // That is a forced choice, so it sets the mode — which also means Clone reproducing a v5 clip
  // keeps rendering v5 even with an empty dialogue box.
  const setVideoVersion = (version) =>
    setVideoModelMode(version === VIDEO_V5 ? VIDEO_V5 : VIDEO_V4)
  const [videoLength, setVideoLength] = usePersistedState(
    'videoLength',
    LTX_LENGTH_DEFAULT,
    asNumber,
  )
  const [videoFps, setVideoFps] = usePersistedState(
    'videoFps',
    LTX_FPS_DEFAULT,
    asNumber,
  )
  const [cameraMove, setCameraMove] = usePersistedState('cameraMove', '')
  // Always defaults to High (Max); a Standard downgrade is never persisted (see
  // effect below), so a stored value is intentionally ignored.
  const [videoQuality, setVideoQuality] = useState('High')
  const [videoProgress, setVideoProgress] = useState(0.0)
  const [queueLength, setQueueLength] = useState(null)
  const [videoRefImage, setVideoRefImage] = useState(null)
  const [videoRefId, setVideoRefId] = useState(null)
  const [nsfwEnabled, setNsfwEnabled] = useState(true)
  const [animateVersion, setAnimateVersion] = usePersistedState(
    'animateVersion',
    3,
    asNumber,
  )
  const [visibilityMode, setVisibilityMode] = useState('Offline')

  //persistent states
  const [autoSave, setAutoSave] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('autoSave')
      return storedValue !== null ? storedValue === 'true' : false
    }
    return false
  })

  const [acceptedPublishTerms, setAcceptedPublishTerms] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('acceptedPublishTerms')
      return storedValue !== null ? storedValue === 'true' : false
    }
    return false
  })

  const [audioEnabled, setAudioEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('audioEnabled')
      return storedValue !== null ? storedValue === 'true' : Math.random() < 0.5
    }
    return false
  })

  const [fill, setFill] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('fill')
      return storedValue !== null ? parseInt(storedValue, 10) : 2
    }
    return 2
  })

  const [paintMode, setPaintMode] = useState('custom')

  const [watermark, setWatermark] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('watermark')
      return storedValue === 'true'
    }
    return false
  })

  const [loop, setLoop] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('loop')
      return storedValue !== null ? storedValue : 'Repeat'
    }
    return 'Repeat'
  })

  const [videoImageSizeV2, setVideoImageSizeV2] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('videoImageSizeV2')
      // If storedValue exists, return it, otherwise return a default value, e.g., 'Offline'
      return storedValue !== null ? storedValue : 'Portrait'
    }
    return 'Portrait'
  })

  const [videoFramerateV2, setVideoFramerateV2] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('videoFramerateV2')
      return storedValue !== null ? parseInt(storedValue, 10) : 25
    }
    return 25
  })

  // Voice-model render resolution. Persisted like every other video setting, and normalised on
  // read so a stale/garbage value falls back to a size we actually render.
  //
  // Defaults to FAST (704x1280), which is a CAPACITY decision, not a quality one. High renders
  // 960x1664 — matching what v4 delivers to the 67% of jobs that upscale — but it costs ~104s of
  // GPU against ~31s, and the voice model has a single pod. On one GPU that difference is the
  // whole product: at Fast a second user waits ~30s, at High ~104s, and the queue rejects past
  // depth 3. High stays one tap away for anyone who wants the bigger picture.
  //
  // resetOnce: the default shipped as High for a few hours, so the handful of users carrying
  // that stored value are moved across once rather than pinned to it forever.
  const [videoResolution, setVideoResolution] = usePersistedState(
    'videoResolution',
    LTX_RESOLUTION_DEFAULT,
    { decode: normalizeResolution, resetOnce: 'videoResolutionDefaultFast' },
  )

  const [age, setAge] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('age')
      return storedValue !== null ? parseInt(storedValue, 10) : 21
    }
    return 21
  })

  const [weight, setWeight] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('weight')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [breasts, setBreasts] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('breasts')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [ass, setAss] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('ass')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [penis, setPenis] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('penis')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [males, setMales] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('males')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [females, setFemales] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('females')
      return storedValue !== null ? parseInt(storedValue, 10) : 1
    }
    return 1
  })

  const [showAttributes, setShowAttributes] = useState(false)

  const [ageVideo, setAgeVideo] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('age')
      return storedValue !== null ? parseInt(storedValue, 10) : 21
    }
    return 21
  })

  const [weightVideo, setWeightVideo] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('weight')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [breastsVideo, setBreastsVideo] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('breasts')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [assVideo, setAssVideo] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('ass')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [showAttributesVideo, setShowAttributesVideo] = useState(false)

  const [videoStartingPoint, setVideoStartingPoint] = useState('image')

  useEffect(() => {
    safeSetItem('videoImageSizeV2', videoImageSizeV2)
  }, [videoImageSizeV2])

  useEffect(() => {
    // Never persist a Standard downgrade — video restarts at High next load.
    if (videoQuality === 'High') {
      safeSetItem('videoQuality', 'High')
    } else {
      safeRemoveItem('videoQuality')
    }
  }, [videoQuality])

  useEffect(() => {
    safeSetItem('loop', loop)
  }, [loop])

  useEffect(() => {
    safeSetItem('videoFramerateV2', videoFramerateV2)
  }, [videoFramerateV2])

  useEffect(() => {
    safeSetItem('age', age)
  }, [age])

  useEffect(() => {
    safeSetItem('weight', weight)
  }, [weight])

  useEffect(() => {
    safeSetItem('breasts', breasts)
  }, [breasts])

  useEffect(() => {
    safeSetItem('ass', ass)
  }, [ass])

  useEffect(() => {
    safeSetItem('showAttributes', showAttributes)
  }, [showAttributes])

  useEffect(() => {
    safeSetItem('chillMode', chillMode)
  }, [chillMode])

  useEffect(() => {
    safeSetItem('recentCharacters', JSON.stringify(recentCharacters))
  }, [recentCharacters])

  // Record every applied character (image and video share one list) into the
  // recent list. Only hosted URLs are kept (base64 data URLs are transient and
  // would bloat localStorage). Moves an existing entry to the front rather than
  // duplicating, and caps the list.
  const recordRecentCharacter = (url) => {
    if (!url || !url.startsWith('http')) return
    if (PRESET_CHARACTER_URLS.includes(url)) return
    setRecentCharacters((prev) =>
      [url, ...prev.filter((existing) => existing !== url)].slice(
        0,
        RECENT_CHARACTERS_LIMIT,
      ),
    )
  }
  // Remembers which image doc a character URL came from; capped alongside the
  // recents list so the map can't grow unbounded in localStorage.
  const registerCharImageId = (url, id) => {
    if (!url || !id || !url.startsWith('http')) return
    setCharImageIds((prev) => {
      const next = { ...prev, [url]: id }
      const keys = Object.keys(next)
      while (keys.length > RECENT_CHARACTERS_LIMIT * 2) {
        delete next[keys.shift()]
      }
      return next
    })
  }
  useEffect(() => {
    safeSetItem('charImageIds', JSON.stringify(charImageIds))
  }, [charImageIds])

  useEffect(() => {
    recordRecentCharacter(charImage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charImage])
  useEffect(() => {
    recordRecentCharacter(charImageVideo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charImageVideo])

  useEffect(() => {
    safeSetItem('watermark', watermark)
  }, [watermark])

  useEffect(() => {
    safeSetItem('watermark', watermark)
  }, [watermark])

  useEffect(() => {
    safeSetItem('paintMode', paintMode)
  }, [paintMode])

  useEffect(() => {
    safeSetItem('fill', fill)
  }, [fill])

  useEffect(() => {
    safeSetItem('audioEnabled', audioEnabled)
  }, [audioEnabled])

  useEffect(() => {
    safeSetItem('nsfwEnabled', nsfwEnabled)
  }, [nsfwEnabled])

  // Unified session reset shared by the image (Inputs) and video (InputsVideo)
  // create views. `scope` controls how much is wiped:
  //   'media' — only the generated images/videos/history (keeps prompts & settings)
  //   'all'   — everything: prompts, settings, attributes AND media
  // The `model`/`quality` overrides let each view keep its own default (image
  // defaults to Pony Anime XL, video to Real). Component-local concerns the
  // context can't see — the on-screen feed, unsaved-changes flag and IndexedDB
  // media cache — are cleared by the callers for both scopes.
  const resetCreateSession = ({
    scope = 'all',
    model: nextModel = 'Pony Anime XL',
    quality: nextQuality = 'Ultra',
  } = {}) => {
    // Generated media & history — cleared for BOTH 'media' and 'all'.
    setImageSrc([])
    setVideoSrc([])
    setImageHistory([])

    if (scope === 'media') return

    // Uploaded reference images.
    setCharImage(null)
    setCharImageVideo(null)

    // Prompts & core settings. videoPrompt/videoNegativePrompt are the "main
    // prompt" on the Video/Animate tab (the bottom PromptBar binds to them there),
    // so a full reset must clear them too — otherwise the prompt sticks after reset.
    // detection/avoidance are the auto-detection prompts; wipe them for a clean slate.
    setModel(nextModel)
    setPrompt('')
    setNegativePrompt('')
    setVideoPrompt('')
    setVideoNegativePrompt('')
    setDetectionPrompt('')
    setAvoidancePrompt('')
    setLora('Default')
    setCreativity(50)
    setSeed(randomSeed())
    setSeedVideo(randomSeed())
    setImageSize('512x768')
    setEmotion('Default')
    // Reset to v2; the model-change effect in Inputs promotes this to the
    // strongest version the reset model supports (v3 on realistic styles).
    setCharacterVersion('v2')
    setFilter('Default')
    setFaceFix(false)
    setQuality(nextQuality)
    setDetails(0)
    setNtier(1)

    // Video settings.
    setVideoImageSizeV2('Portrait')
    setVideoQuality('High')
    setAudioEnabled(true)
    // Voice-model inputs. The model selection resets to Auto: `model` and `quality` are both
    // clobbered here, so leaving a forced video model pinned across a full reset was
    // inconsistent. Reset also clears the dialogue line below, so Auto lands back on v4.
    setVideoModelMode(VIDEO_MODEL_AUTO)
    setDialogue('')
    setDialogueSpeaker(LTX_DIALOGUE_SPEAKER_DEFAULT)
    setDialogueTone(LTX_DIALOGUE_TONE_DEFAULT)
    setDialogueAccent(LTX_DIALOGUE_ACCENT_DEFAULT)
    setCameraMove('')
    setVideoLength(LTX_LENGTH_DEFAULT)
    setVideoFps(LTX_FPS_DEFAULT)
    // Back to Fast, matching the shipped default — see the state declaration for why Fast.
    setVideoResolution(LTX_RESOLUTION_DEFAULT)

    // Attributes — previously never reset (users reported these sticking). Reset
    // both the image and video variants to their defaults. The persisted keys
    // (age/weight/breasts/ass) are rewritten by their effects; the non-persisted
    // ones (penis/males/females) only live in state, so clear any legacy
    // localStorage entries too.
    setAge(21)
    setWeight(0)
    setBreasts(0)
    setAss(0)
    setPenis(0)
    setMales(0)
    setFemales(1)
    setAgeVideo(21)
    setWeightVideo(0)
    setBreastsVideo(0)
    setAssVideo(0)
    setShowAttributes(false)
    setShowAttributesVideo(false)
    if (typeof window !== 'undefined') {
      safeRemoveItem('penis')
      safeRemoveItem('males')
      safeRemoveItem('females')
    }
  }

  return (
    <CreateContext.Provider
      value={{
        hasMadeChanges,
        setHasMadeChanges,
        model,
        setModel,
        imageSize,
        setImageSize,
        quality,
        setQuality,
        creativity,
        setCreativity,
        prompt,
        setPrompt,
        negativePrompt,
        setNegativePrompt,
        seed,
        setSeed,
        imageSrc,
        setImageSrc,
        isLoading,
        setIsLoading,
        errorText,
        setErrorText,
        lora,
        setLora,
        docRef,
        setDocRef,
        isDeleting,
        setIsDeleting,
        locked,
        setLocked,
        activeTab,
        setActiveTab,
        ntier,
        setNtier,
        faceFix,
        setFaceFix,
        chillMode,
        setChillMode,
        imageHistory,
        setImageHistory,
        docRefHistory,
        setDocRefHistory,
        isUpscaling,
        setIsUpscaling,
        safeImage,
        setSafeImage,
        watermark,
        setWatermark,
        brushSize,
        setBrushSize,
        undo,
        setUndo,
        reset,
        setReset,
        currentImageSrcIndex,
        setCurrentImageSrcIndex,
        mask,
        setMask,
        brushStrength,
        setBrushStrength,
        fill,
        setFill,
        hideMask,
        setHideMask,
        tool,
        setTool,
        cooldown,
        setCooldown,
        cooldownProgress,
        setCooldownProgress,
        initialCooldown,
        setInitialCooldown,
        settingsOpen,
        setSettingsOpen,
        filter,
        setFilter,
        blurryImage,
        setBlurryImage,
        paintMode,
        setPaintMode,
        details,
        setDetails,
        customPoseImage,
        setCustomPoseImage,
        charImage,
        setCharImage,
        charImageIds,
        registerCharImageId,
        recentCharacters,
        setRecentCharacters,
        charImageVideo,
        setCharImageVideo,
        animation,
        setAnimation,
        animateMode,
        setAnimateMode,
        formatTab,
        setFormatTab,
        videoModel,
        setVideoModel,
        videoImageSize,
        setVideoImageSize,
        videoPrompt,
        setVideoPrompt,
        videoNegativePrompt,
        setVideoNegativePrompt,
        videoIsLoading,
        setVideoIsLoading,
        videoLora,
        setVideoLora,
        videoFilter,
        setVideoFilter,
        videoCreativity,
        setVideoCreativity,
        videoSrc,
        setVideoSrc,
        expandAmount,
        setExpandAmount,
        fullMask,
        setFullMask,
        cameraMotion,
        setCameraMotion,
        lastQuality,
        setLastQuality,
        characterSuggestion,
        setCharacterSuggestion,
        locationSuggestion,
        setLocationSuggestion,
        styleSuggestion,
        setStyleSuggestion,
        actionSuggestion,
        setActionSuggestion,
        stickMode,
        setStickMode,
        folderSetting,
        setFolderSetting,
        folders,
        setFolders,
        emotion,
        setEmotion,
        characterVersion,
        setCharacterVersion,
        detectionPrompt,
        setDetectionPrompt,
        avoidancePrompt,
        setAvoidancePrompt,
        videoRefImage,
        setVideoRefImage,
        videoRefLora,
        setVideoRefLora,
        videoId,
        setVideoId,
        videoVersion,
        setVideoVersion,
        videoModelMode,
        setVideoModelMode,
        dialogue,
        setDialogue,
        dialogueSpeaker,
        setDialogueSpeaker,
        dialogueTone,
        dialogueAccent,
        setDialogueAccent,
        setDialogueTone,
        videoLength,
        setVideoLength,
        videoFps,
        setVideoFps,
        cameraMove,
        setCameraMove,
        videoQuality,
        setVideoQuality,
        videoImageSizeV2,
        setVideoImageSizeV2,
        videoProgress,
        setVideoProgress,
        age,
        setAge,
        showAttributes,
        setShowAttributes,
        males,
        setMales,
        females,
        setFemales,
        showAttributesVideo,
        setShowAttributesVideo,
        weight,
        setWeight,
        breasts,
        setBreasts,
        ass,
        setAss,
        ageVideo,
        setAgeVideo,
        weightVideo,
        setWeightVideo,
        breastsVideo,
        setBreastsVideo,
        assVideo,
        setAssVideo,
        videoFramerateV2,
        setVideoFramerateV2,
        videoResolution,
        setVideoResolution,
        queueLength,
        setQueueLength,
        loop,
        setLoop,
        audioEnabled,
        setAudioEnabled,
        animateVersion,
        setAnimateVersion,
        videoRefId,
        setVideoRefId,
        seedVideo,
        setSeedVideo,
        videoStartingPoint,
        setVideoStartingPoint,
        nsfwEnabled,
        setNsfwEnabled,
        lorasList,
        setLorasList,
        lorasListVideo,
        setLorasListVideo,
        acceptedPublishTerms,
        setAcceptedPublishTerms,
        autoSave,
        setAutoSave,
        visibilityMode,
        setVisibilityMode,
        penis,
        setPenis,
        resetCreateSession,
      }}
    >
      {children}
    </CreateContext.Provider>
  )
}
